import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Theme, Typography, withStyles, WithStyles } from '@material-ui/core';
import { Button, DateRangePicker, MessageTemplate } from 'core';
import ContentTableLoader from 'core/AvailabilitySelector/ContentTableLoader';
import { concat, filter, isEmpty, map, uniq } from 'lodash';
import { useAppointmentTypesByIdsV2 } from 'ui-model';

import { UseSettingSchedulerDatesResponse } from 'hooks/useSettingSchedulerDates';

import useAvailabilitySchedulerPagination from 'modules/LumaBotV2/WIP/ui-model/useAvailabilitySchedulerPagination';

import ListAvailabilitiesGrouped, { ListAvailabilitiesGroupedProps } from './ListAvailabilitiesGrouped';

const messages = defineMessages({
  loadMore: {
    id: 'modules.LumaBot.MessageScheduler.QueryListAvailabilitiesGrouped.loadMore',
    defaultMessage: 'Load More',
  },
  speakWithStaff: {
    id: 'modules.LumaBot.MessageScheduler.QueryListAvailabilitiesGrouped.speakWithStaff',
    defaultMessage: 'Speak with Staff',
  },
  restartConversation: {
    id: 'modules.LumaBot.MessageScheduler.QueryListAvailabilitiesGrouped.restartConversation',
    defaultMessage: 'Restart Conversation',
  },
  noAppointmentsFound: {
    id: 'modules.LumaBot.MessageScheduler.QueryListAvailabilitiesGrouped.noAppointmentsFound',
    defaultMessage: '0 appointment times found. Try adjusting your filters to view more results.',
  },
});

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    loadMoreWrapper: {
      width: '100%',
      textAlign: 'center',
    },
    dateRangePickerContainer: {
      marginBottom: `${unit * 1.5}px`,
    },
  });

export interface QueryListAvailabilitiesGroupedProps
  extends Omit<ListAvailabilitiesGroupedProps, 'availabilities'>,
    WithStyles<typeof styles> {
  params: any;
  dateRange: UseSettingSchedulerDatesResponse;
  setDateRange: (params: UseSettingSchedulerDatesResponse) => void;
  isMobile?: boolean;
  showFilters?: boolean;
}

const QueryListAvailabilitiesGrouped: React.FC<QueryListAvailabilitiesGroupedProps> = ({
  classes,
  params,
  isMobile,
  showFilters,
  dateRange,
  setDateRange,
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const {
    isLoading,
    isFetchingNextPage,
    data: dataPagination,
    hasNextPage,
    fetchNextPage,
  } = useAvailabilitySchedulerPagination(params);

  const availabilities = useMemo(() => concat(...map(dataPagination?.pages, 'response')), [dataPagination]);
  const apptTypeIds = useMemo(
    () => filter(uniq(map(availabilities, (availability) => availability.appointmentType)), Boolean),
    [availabilities],
  );

  const { data: appointmentTypes = [] } = useAppointmentTypesByIdsV2({
    ids: apptTypeIds,
    limit: apptTypeIds.length,
  });

  const populatedAvailabilities = useMemo(
    () =>
      map(availabilities, (availability) => ({
        ...availability,
        appointmentType: filter(
          appointmentTypes,
          (appointmentType) => appointmentType._id === availability.appointmentType,
        )[0],
      })),
    [availabilities, appointmentTypes],
  );

  return (
    <div>
      <div className={classes.dateRangePickerContainer}>
        <DateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onDatesChange={setDateRange}
          numberOfMonths={1}
        />
      </div>
      {!isLoading && isEmpty(populatedAvailabilities) ? (
        <Typography variant="h6">
          <MessageTemplate
            id={'messages.web.emptyAppointmentResultsFiltersAvailable'}
            defaultMessage={formatMessage(messages.noAppointmentsFound)}
          />
        </Typography>
      ) : (
        <>
          <ListAvailabilitiesGrouped availabilities={populatedAvailabilities} {...rest} />
          {(isLoading || isFetchingNextPage) && <ContentTableLoader isMobile={isMobile} />}
          {hasNextPage && !isLoading && !isFetchingNextPage && (
            <div className={classes.loadMoreWrapper}>
              <Button variant="contained" color="primary" fullWidth={isMobile} onClick={() => fetchNextPage()}>
                {formatMessage(messages.loadMore)}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default withStyles(styles)(QueryListAvailabilitiesGrouped);
